<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">已合并的 Pull Requests</h1>
        <p class="mt-1 text-sm text-gray-600">
          Apache Kafka PMC 成员已合并的 Pull Requests
        </p>
      </div>
      <div class="flex items-center space-x-4">
        <div class="flex items-center space-x-2">
          <label class="text-sm font-medium text-gray-700">排序方式:</label>
          <select
            v-model="sortBy"
            @change="handleSortChange"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="merged">合并时间</option>
            <option value="updated">更新时间</option>
            <option value="created">创建时间</option>
          </select>
        </div>
        <button
          @click="refreshData"
          :disabled="loading"
          class="btn btn-primary flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <span>刷新</span>
        </button>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div class="card p-4">
        <div class="text-2xl font-bold text-primary-600">{{ totalMergedPRs }}</div>
        <div class="text-sm text-gray-600">已合并PR</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-green-600">{{ recentMergedPRs }}</div>
        <div class="text-sm text-gray-600">近7天合并</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-blue-600">{{ totalAdditions }}</div>
        <div class="text-sm text-gray-600">总增加行数</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-red-600">{{ totalDeletions }}</div>
        <div class="text-sm text-gray-600">总删除行数</div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-flex items-center space-x-2">
        <svg class="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-600">加载中...</span>
      </div>
    </div>

    <!-- PRs列表 -->
    <div v-else-if="sortedPRs.length > 0" class="space-y-4">
      <div
        v-for="pr in sortedPRs"
        :key="pr.id"
        class="card p-6 hover:shadow-md transition-shadow border-l-4 border-green-400"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-3 mb-2">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                已合并
              </span>
              <span v-if="pr.labels && pr.labels.length > 0" class="flex space-x-1">
                <span
                  v-for="label in pr.labels.slice(0, 3)"
                  :key="label.name"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  :style="{ backgroundColor: `#${label.color}`, color: getContrastColor(label.color) }"
                >
                  {{ label.name }}
                </span>
              </span>
            </div>
            
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              <a :href="pr.url" target="_blank" class="hover:text-primary-600">
                #{{ pr.number }}: {{ pr.title }}
              </a>
            </h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
              <div>
                <span class="font-medium">作者:</span>
                <a :href="pr.authorUrl" target="_blank" class="text-primary-600 hover:text-primary-700">
                  {{ pr.author }}
                </a>
                <span v-if="pr.isAuthorPMC" class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  PMC
                </span>
              </div>
              <div v-if="pr.assignees && pr.assignees.length > 0">
                <span class="font-medium">负责人:</span>
                <span v-for="(assignee, index) in pr.assignees" :key="assignee.login">
                  <a :href="assignee.url" target="_blank" class="text-primary-600 hover:text-primary-700">
                    {{ assignee.login }}
                  </a>
                  <span v-if="index < pr.assignees.length - 1">, </span>
                </span>
              </div>
              <div>
                <span class="font-medium">创建时间:</span>
                {{ formatDate(pr.createdAt) }}
              </div>
              <div v-if="pr.mergedAt">
                <span class="font-medium">合并时间:</span>
                {{ formatDate(pr.mergedAt) }}
              </div>
              <div v-if="pr.mergedBy">
                <span class="font-medium">合并者:</span>
                <span class="text-primary-600">{{ pr.mergedBy }}</span>
                <span v-if="pr.pmcInvolvement && pr.pmcInvolvement.includes('merger')" class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  PMC
                </span>
              </div>
              <!-- PMC参与信息 -->
              <div v-if="pr.pmcMembers && pr.pmcMembers.length > 0" class="md:col-span-2">
                <span class="font-medium">PMC成员参与:</span>
                <div class="mt-1 flex flex-wrap gap-2">
                  <span v-for="member in pr.pmcMembers" :key="member" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    {{ member }}
                  </span>
                </div>
                <div v-if="pr.pmcInvolvement && pr.pmcInvolvement.length > 0" class="mt-1 text-xs text-gray-500">
                  参与方式: {{ pr.pmcInvolvement.join(', ') }}
                </div>
              </div>
            </div>

            <!-- 代码变更统计 -->
            <div class="flex items-center space-x-6 text-sm">
              <div v-if="pr.additions !== undefined" class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-green-500 rounded-full"></span>
                <span class="text-green-600 font-medium">+{{ pr.additions }}</span>
              </div>
              <div v-if="pr.deletions !== undefined" class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-red-500 rounded-full"></span>
                <span class="text-red-600 font-medium">-{{ pr.deletions }}</span>
              </div>
              <div v-if="pr.changedFiles !== undefined" class="flex items-center space-x-1">
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span class="text-gray-600">{{ pr.changedFiles }} 文件</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">暂无已合并的PR</h3>
      <p class="mt-1 text-sm text-gray-500">
        {{ loading ? '正在加载...' : '点击刷新按钮获取最新数据' }}
      </p>
      <div v-if="!loading && mergedPRs.length === 0" class="mt-4">
        <p class="text-xs text-gray-400">
          提示：GitHub API 可能需要认证令牌才能获取数据
        </p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">加载失败</h3>
          <p class="mt-1 text-sm text-red-700">{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores'

const store = useAppStore()
const sortBy = ref('merged')

// 使用 storeToRefs 保持响应式
const { loading, error, mergedPRs, totalMergedPRs } = storeToRefs(store)

// 计算属性
const sortedPRs = computed(() => {
  if (!mergedPRs.value) return []
  
  const prs = [...mergedPRs.value]
  return prs.sort((a, b) => {
    let dateA, dateB
    
    switch (sortBy.value) {
      case 'merged':
        dateA = new Date(a.mergedAt || a.updatedAt)
        dateB = new Date(b.mergedAt || b.updatedAt)
        break
      case 'updated':
        dateA = new Date(a.updatedAt)
        dateB = new Date(b.updatedAt)
        break
      case 'created':
        dateA = new Date(a.createdAt)
        dateB = new Date(b.createdAt)
        break
      default:
        dateA = new Date(a.mergedAt || a.updatedAt)
        dateB = new Date(b.mergedAt || b.updatedAt)
    }
    
    return dateB - dateA
  })
})

const recentMergedPRs = computed(() => {
  if (!mergedPRs.value || !Array.isArray(mergedPRs.value)) return 0

  const oneWeekAgo = new Date()
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

  return mergedPRs.value.filter(pr => {
    const mergedDate = new Date(pr.mergedAt || pr.updatedAt)
    return mergedDate >= oneWeekAgo
  }).length
})

const totalAdditions = computed(() => {
  if (!mergedPRs.value || !Array.isArray(mergedPRs.value)) return 0
  return mergedPRs.value.reduce((sum, pr) => sum + (pr.additions || 0), 0)
})

const totalDeletions = computed(() => {
  if (!mergedPRs.value || !Array.isArray(mergedPRs.value)) return 0
  return mergedPRs.value.reduce((sum, pr) => sum + (pr.deletions || 0), 0)
})

// 方法
const formatDate = (dateString) => {
  if (!dateString) return '未知'
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = (now - date) / (1000 * 60 * 60)
  
  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}小时前`
  } else if (diffInHours < 24 * 7) {
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}天前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}

const getContrastColor = (hexColor) => {
  // 计算对比色以确保标签文字可读
  if (!hexColor) return '#000000'
  const cleanHex = hexColor.replace('#', '')
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 128 ? '#000000' : '#ffffff'
}

const handleSortChange = () => {
  // 排序变化时的处理逻辑
}

const refreshData = async () => {
  try {
    await store.fetchMergedPRs(true)
  } catch (error) {
    console.error('Failed to refresh merged PRs data:', error)
  }
}

// 生命周期
onMounted(() => {
  if (mergedPRs.value.length === 0) {
    refreshData()
  }
})
</script> 